import React, { useCallback, useState } from 'react';
import { isEmpty } from 'lodash';
import { useReservation, useReservationActivity } from './reservation.context';
import { SaveButton } from '../../components/SaveButton.component';
import { useReservationHash } from './reservationHash.context';
import { ContractButton } from './components/contractButton.component';
import { canoeCheckWarningBreakedRules } from '@crm/utils';
import { AlertDialog } from '../../components/alerts/AlertDialog.component';

/**
 * Composant de bouton de sauvegarde pour les réservations
 * 
 * Ce composant gère :
 * - La sauvegarde des réservations avec validation complète
 * - La vérification des règles de canoë pour les activités supervisées
 * - L'affichage des avertissements avant sauvegarde
 * - La gestion des contrats et documents
 * - Le rafraîchissement automatique après sauvegarde
 * 
 * Logique métier :
 * - Validation des changements importants avant sauvegarde
 * - Vérification des règles de canoë (capacité, âge, etc.)
 * - Gestion des avertissements avec dialogue de confirmation
 * - Sauvegarde conditionnelle selon l'état de la réservation
 * - Intégration avec le système de contrats
 * 
 * @returns Composant React du bouton de sauvegarde de réservation
 */
export const SaveReservationButton: React.FC = () => {
  const { save, reservation, reload } = useReservation();
  const { activity, activityTimeSlot } = useReservationActivity();
  const { isReady, hasImportantChanges, errors } = useReservationHash();
  
  /**
   * Messages d'erreur empêchant la sauvegarde
   */
  const cannotSaveMessages = [...errors];
  
  /**
   * États pour la gestion des avertissements
   */
  const [warnings, setWarnings] = useState<string[]>([]);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  /**
   * Fonctions de gestion du dialogue d'avertissement
   */
  const warningDialog = useCallback(() => setWarningDialogOpen(true), [
    setWarningDialogOpen,
  ]);
  const warningDialogClose = useCallback(
    () => setWarningDialogOpen(false),
    [setWarningDialogOpen],
  );

  /**
   * Vérification de l'état de préparation avant sauvegarde
   */
  if (!isReady) {
    cannotSaveMessages.push(
      `Vérification en cours sur la mise à jour du contrat...`,
    );
  }

  /**
   * Fonction de sauvegarde de la réservation
   * 
   * Cette fonction :
   * - Sauvegarde la réservation si les conditions sont remplies
   * - Rafraîchit automatiquement après sauvegarde pour les réservations existantes
   * - Gère les dépendances pour éviter les boucles infinies
   */
  const saveReservation = useCallback(() => {
    if (
      reservation._id &&
      isEmpty(cannotSaveMessages) &&
      isReady &&
      hasImportantChanges
    ) {
      save().then(() => {
        setTimeout(reload, 2000);
      });
    } else {
      save();
    }
    // #TODO fix eventually these dependancies for not causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    save,
    reload,
    reservation._id,
    hasImportantChanges,
    isReady
  ]);

  /**
   * Fonction de vérification des avertissements avant sauvegarde
   * 
   * Cette fonction :
   * - Vérifie les règles de canoë pour les activités supervisées
   * - Affiche un dialogue d'avertissement si des règles sont enfreintes
   * - Sauvegarde directement si aucun avertissement
   * - Gère les contraintes de réservation et capacités minimales
   */
  const checkWarningsAndSave = useCallback(() => {
    const activityPricing = reservation.pricing || activity?.pricing;
    let _warnings: string[] = [];
    if (activityPricing && activity?.kind === "supervised") {
      _warnings = canoeCheckWarningBreakedRules(
        activityPricing,
        reservation.people || {},
        (
          activityTimeSlot?.constrainReservations === true &&
          activityTimeSlot?.minAutonomousPeople
        ) ? (activityTimeSlot?.minAutonomousPeople) : 0,
      );
      setWarnings(_warnings);
    }
    if(_warnings.length === 0) {
      setWarnings([]);
      saveReservation();
    } else {
      warningDialog();
    }

  },[
    reservation.pricing,
    activity?.pricing,
    activityTimeSlot?.constrainReservations,
    activityTimeSlot?.minAutonomousPeople,
    activity?.kind,
    reservation?.people,
    saveReservation,
    warningDialog
  ]);
  
  /**
   * Composant de dialogue d'avertissement
   * 
   * Affiche les avertissements de règles enfreintes avec options
   * d'acceptation ou d'annulation
   */
  const warningDialogElement = (<AlertDialog
    accept={()=> {saveReservation();warningDialogClose(); }}
    cancel={warningDialogClose}
    open={warningDialogOpen}
    title={'Cette inscription ne respecte pas les règles suivantes: \n Souhaitez vous quand même valider ?'}
    content={
      warnings.map(
        (message) => ` - ${message}`
      ).join('\n')/*+'\n\n Souhaitez vous quand même valider ?'*/
    }
  />);

  if (isEmpty(cannotSaveMessages) && isReady && hasImportantChanges) {
    return (
      <div>
          {warningDialogElement}
          <SaveButton cannotSaveMessages={[]} onClick={checkWarningsAndSave} />
          <ContractButton fullWidth>
          </ContractButton>
      </div>
    );
  }

  return <div>
    {warningDialogElement}
    <SaveButton cannotSaveMessages={cannotSaveMessages} onClick={checkWarningsAndSave} />
  </div>;
};
